import React from 'react';
import { BOOKING_URL } from '../../../constants/config';
import {
  Container,
  Typography,
  Grid,
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety'; // For benefits/importance
import EventAvailableIcon from '@mui/icons-material/EventAvailable'; // For who should get
import FactCheckIcon from '@mui/icons-material/FactCheck'; // For what's included
import CleaningServicesIcon from '@mui/icons-material/CleaningServices'; // For process
import SpaIcon from '@mui/icons-material/Spa'; // For home care / why choose us
import StarIcon from '@mui/icons-material/Star'; // For why choose us
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const DentalExamsAndCleanings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Updated benefits specific to Dental Exams and Cleanings
  const benefits = [
    'Prevents cavities and tooth decay by removing plaque and tartar.',
    'Detects gum disease in its early, treatable stages.',
    'Identifies potential issues like oral cancer early on.',
    'Helps maintain fresh breath and a brighter, healthier smile.',
    'Saves money by avoiding more complex and costly treatments later.',
    'Contributes to overall health, as oral health is linked to systemic conditions.'
  ];

  // Updated process specific to Dental Exams and Cleanings
  const process = [
    {
      title: '1. Welcoming & Medical History Review',
      description: 'We start by discussing any concerns you have and reviewing your medical and dental history to ensure we provide personalized care.'
    },
    {
      title: '2. Comprehensive Dental Examination',
      description: 'Our dentist will thoroughly examine your teeth, gums, and soft tissues. This includes checking for signs of decay, gum disease, and conducting an oral cancer screening. We may take digital X-rays if they are due, to see below the surface.'
    },
    {
      title: '3. Professional Teeth Cleaning (Prophylaxis)',
      description: 'Our skilled dentist will gently remove plaque and tartar buildup from your teeth, both above and below the gumline. This is followed by polishing to remove surface stains, leaving your teeth smooth and clean.'
    },
    {
      title: '4. Flossing & Fluoride Treatment (if applicable)',
      description: 'We\'ll floss between your teeth to remove any remaining debris and may recommend a fluoride treatment to help strengthen your enamel and prevent cavities.'
    },
    {
        title: '5. Personalized Oral Hygiene Education & Plan',
        description: 'We\'ll discuss our findings, provide tailored advice on your home care routine, and schedule your next preventive visit.'
    }
  ];

  const whoShouldGet = [
    'Everyone, starting from early childhood (around age 1 or first tooth).',
    'Adults of all ages to maintain optimal oral health.',
    'Individuals with existing dental work like fillings, crowns, or implants.',
    'Patients with a history of gum disease or high risk of cavities.',
    'Anyone wanting to prevent dental problems and maintain a healthy smile.',
    'Pregnant women, as hormonal changes can affect oral health.'
  ];

  const whatsIncluded = [
    {
      category: 'Comprehensive Dental Exam Includes:',
      items: [
        'Thorough tooth-by-tooth check for decay and damage.',
        'Gum health assessment (periodontal screening).',
        'Oral cancer screening of soft tissues.',
        'Bite and TMJ (jaw joint) evaluation.',
        'Review of digital X-rays (as needed).',
        'Consultation and treatment planning.'
      ],
      icon: <FactCheckIcon sx={{ color: 'primary.main' }}/>
    },
    {
      category: 'Professional Cleaning Includes:',
      items: [
        'Removal of plaque and tartar (scaling).',
        'Teeth polishing to remove surface stains.',
        'Expert flossing.',
        'Oral hygiene instruction and tips.',
        'Optional fluoride application.'
      ],
      icon: <CleaningServicesIcon sx={{ color: 'primary.main' }}/>
    }
  ];

  const homeCareTips = [
    'Brush your teeth thoroughly twice a day for two minutes with fluoride toothpaste.',
    'Floss daily to clean between teeth and under the gumline.',
    'Eat a balanced diet and limit sugary snacks and drinks.',
    'Avoid tobacco products, which significantly increase dental risks.',
    'Use an antiseptic mouthwash if recommended by your dentist.',
    'Replace your toothbrush every 3-4 months or sooner if bristles are frayed.'
  ];
  
  const faqs = [
    {
      question: 'How often should I have a dental exam and cleaning?',
      answer: 'For most people, visiting the dentist every six months is recommended. However, depending on your individual oral health needs, your dentist might suggest more or less frequent visits.'
    },
    {
      question: 'Is a professional dental cleaning painful?',
      answer: 'A routine dental cleaning is typically painless. If you have sensitive gums or significant tartar buildup, you might experience some mild discomfort, but our team is very gentle and focused on your comfort.'
    },
    {
      question: 'How long does a dental exam and cleaning take?',
      answer: 'A typical appointment for a dental exam and cleaning lasts about 45-60 minutes, allowing enough time for a thorough assessment and cleaning.'
    },
    {
        question: 'Are dental X-rays always necessary?',
        answer: 'Dental X-rays are not needed at every visit. They are taken based on your individual risk factors, age, and dental history to detect issues not visible during a visual exam. We use digital X-rays, which minimize radiation exposure.'
    },
    {
        question: 'Why is it important to remove tartar?',
        answer: 'Tartar (calculus) is hardened plaque that cannot be removed by brushing and flossing alone. It can lead to gum disease, cavities, and bad breath. Professional cleaning is essential to remove it.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Dental Exams & Cleanings | Preventive Care | Pacifica Dental</title>
        <meta name="description" content="Maintain optimal oral health with regular dental exams and professional cleanings at Pacifica Dental. Prevent cavities, gum disease, and keep your smile bright." />
        <meta name="keywords" content="dental exam, dental cleaning, teeth cleaning, preventive dentistry, oral hygiene, [Your City] dentist, family dentist, check-up, prophylaxis" />
      </Helmet>
      
      <Box 
        sx={{ 
          py: { xs: 6, md: 8 },
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e9eef3 100%)',
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant={isMobile ? "h3" : "h2"}
              component="h1"
              textAlign="center"
              gutterBottom
              sx={{ 
                mb: 2,
                fontWeight: 700,
                color: 'primary.main'
              }}
            >
              Your Foundation for a Lifetime of Healthy Smiles
            </Typography>

            <Typography
              variant={isMobile ? "h6" : "h5"}
              component="h2"
              textAlign="center"
              gutterBottom
              sx={{ mb: 4, color: 'text.secondary', fontWeight: 500 }}
            >
              Comprehensive Dental Exams & Professional Cleanings
            </Typography>

            <Typography
              variant="body1"
              paragraph
              sx={{ mb: 4, fontSize: '1.15rem', lineHeight: 1.8, color: 'text.primary' }}
            >
              At Pacifica Dental, we believe that prevention is the key to lasting oral health and a beautiful smile. Regular dental exams and professional cleanings are the cornerstones of a proactive approach to dental care. These routine visits allow us to detect potential issues early, keep your teeth and gums healthy, and provide you with the knowledge to maintain excellent oral hygiene at home.
            </Typography>
            <Divider sx={{ my: 4 }} />

            {/* Benefits Section */}
            <Grid container spacing={isMobile ? 3 : 5} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 2 : 3,
                    height: '100%',
                    backgroundColor: theme.palette.mode === 'light' ? 'primary.light' : 'primary.dark',
                    color: 'white',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2}}>
                    Key Benefits of Regular Check-ups
                  </Typography>
                  <List dense>
                    {benefits.map((benefit, index) => (
                      <ListItem key={index} sx={{ py: 0.5}}>
                        <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5}}>
                          <HealthAndSafetyIcon sx={{ color: 'white', fontSize: '1.4rem' }} />
                        </ListItemIcon>
                        <ListItemText primary={benefit} primaryTypographyProps={{fontSize: '1.05rem'}} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>

              {/* Process Section */}
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={3}
                  sx={{
                    p: isMobile ? 2 : 3,
                    height: '100%',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h5" gutterBottom color="primary.main" sx={{ fontWeight: 600, mb: 2}}>
                    What to Expect During Your Visit
                  </Typography>
                  {process.map((step, index) => (
                    <Box key={index} sx={{ mb: index === process.length - 1 ? 0 : 2.5 }}>
                      <Typography variant="h6" color="primary.dark" fontWeight="bold" sx={{fontSize: '1.1rem'}}>
                        {step.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{fontSize: '0.95rem', lineHeight: 1.6}}>
                        {step.description}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>
            </Grid>

            <Divider sx={{ my: 5 }} /> 

            {/* Who Should Get Regular Exams & Cleanings? Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Who Benefits from Regular Dental Visits?
              </Typography>
              <Paper elevation={2} sx={{p:3, borderRadius: 2, background: theme.palette.mode === 'light' ? '#e3f2fd' : '#2c3e50' }}>
                <Typography variant="body1" paragraph sx={{ mb: 2, fontSize: '1.1rem', color: 'text.secondary' }}>
                  Regular dental exams and cleanings are essential for almost everyone. We recommend routine visits for:
                </Typography>
                <Grid container spacing={1}>
                    {whoShouldGet.map((item, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                            <List dense sx={{py:0}}>
                                <ListItem sx={{py:0.2}}>
                                    <ListItemIcon sx={{minWidth: 'auto', marginRight: 1, color: 'primary.main'}}>
                                        <EventAvailableIcon />
                                    </ListItemIcon>
                                    <ListItemText primary={item} primaryTypographyProps={{fontSize: '1rem'}}/>
                                </ListItem>
                            </List>
                        </Grid>
                    ))}
                </Grid>
              </Paper>
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* What's Included Section */}
            <Box sx={{ my: 5 }}>
                <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                    Our Comprehensive Preventive Care Package
                </Typography>
                <Grid container spacing={3}>
                    {whatsIncluded.map((section, index) => (
                        <Grid item xs={12} md={6} key={index}>
                            <Paper elevation={2} sx={{ p: 2.5, height: '100%', borderRadius: 2 }}>
                                <Box sx={{display: 'flex', alignItems: 'center', mb:1.5}}>
                                    {section.icon}
                                    <Typography variant="h6" component="h4" sx={{fontWeight: 'bold', fontSize:'1.2rem', color: 'secondary.dark', ml:1}}>
                                        {section.category}
                                    </Typography>
                                </Box>
                                <List dense>
                                    {section.items.map((item, itemIndex) => (
                                        <ListItem key={itemIndex}>
                                            <ListItemIcon sx={{minWidth: 'auto', marginRight: 1, color: 'text.secondary'}}>
                                                <CheckCircleOutlineIcon fontSize="small"/>
                                            </ListItemIcon>
                                            <ListItemText primary={item} primaryTypographyProps={{fontSize: '0.95rem'}}/>
                                        </ListItem>
                                    ))}
                                </List>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Home Care Tips Section */}
            <Box sx={{ my: 5 }}>
                <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                    Maintaining Your Healthy Smile Between Visits
                </Typography>
                 <Paper elevation={2} sx={{p:3, borderRadius: 2, background: theme.palette.mode === 'light' ? '#e8f5e9' : '#1b5e20' }}>
                    <List>
                        {homeCareTips.map((tip, index) => (
                            <ListItem key={index}>
                                <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5, color: 'primary.dark'}}>
                                    <SpaIcon />
                                </ListItemIcon>
                                <ListItemText primary={tip} primaryTypographyProps={{fontSize: '1rem'}}/>
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            </Box>
            
            <Divider sx={{ my: 5 }} />


            {/* FAQ Section */}
            <Box sx={{ my: 5 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb: 3 }}>
                Your Questions About Dental Exams & Cleanings Answered
              </Typography>
              {faqs.map((faq, index) => (
                <Accordion key={index} elevation={2} sx={{ mb: 1.5, borderRadius: 1, '&:before': { display: 'none' } }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon color="primary"/>}
                    aria-controls={`faq-panel${index}-content`}
                    id={`faq-panel${index}-header`}
                    sx={{ '& .MuiAccordionSummary-content': { fontWeight: 'medium', fontSize: '1.1rem' } }}
                  >
                    {faq.question}
                  </AccordionSummary>
                  <AccordionDetails sx={{backgroundColor: theme.palette.mode === 'light' ? '#fafafa' : '#333' }}>
                    <Typography color="text.secondary" sx={{lineHeight: 1.7, fontSize: '1rem'}}>
                      {faq.answer}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>

            <Divider sx={{ my: 5 }} />

            {/* Why Choose Us Section */}
            <Box sx={{ mt: 6, p:3, background: theme.palette.mode === 'light' ? 'rgba(0, 123, 255, 0.05)' : 'rgba(100,100,100,0.1)', borderRadius:2 }}>
              <Typography variant={isMobile ? "h5" : "h4"} component="h3" gutterBottom textAlign="center" sx={{ fontWeight: 600, color: 'primary.main', mb:3 }}>
                Why Choose Us for Your Preventive Dental Care?
              </Typography>
              <Grid container spacing={3}>
                  {[
                      { title: "Thorough & Gentle Approach", text: "Our team is dedicated to providing comprehensive exams and gentle cleanings, ensuring your comfort throughout your visit.", icon: <SpaIcon color="primary" /> },
                      { title: "Focus on Patient Education", text: "We believe in empowering you with knowledge about your oral health and effective home care techniques.", icon: <FactCheckIcon color="primary" /> },
                      { title: "Early Detection Technology", text: "We use modern diagnostic tools, including digital X-rays, to identify potential problems at their earliest stages.", icon: <HealthAndSafetyIcon color="primary" /> },
                      { title: "Friendly & Welcoming Environment", text: "We strive to make every visit a positive experience in a relaxed and supportive atmosphere.", icon: <StarIcon color="primary" /> }
                  ].map(item => (
                      <Grid item xs={12} sm={6} key={item.title}>
                          <Box sx={{display: 'flex', alignItems: 'flex-start'}}>
                              <ListItemIcon sx={{minWidth: 'auto', marginRight: 1.5, marginTop: 0.5}}>
                                  {item.icon}
                              </ListItemIcon>
                              <Box>
                                  <Typography variant="h6" component="h4" sx={{fontWeight: 'bold', fontSize: '1.1rem'}}>{item.title}</Typography>
                                  <Typography variant="body2" color="text.secondary">{item.text}</Typography>
                              </Box>
                          </Box>
                      </Grid>
                  ))}
              </Grid>
            </Box>


            {/* Call to Action */}
            <Box sx={{ mt: 6, py: 4, textAlign: 'center', background: theme.palette.primary.main, color: 'white', borderRadius: 2 }}>
                <Typography variant={isMobile ? "h5" : "h4"} gutterBottom sx={{fontWeight: 600}}>
                    Ready to Invest in Your Oral Health?
                </Typography>
                <Typography variant="body1" sx={{mb:3, opacity: 0.9}}>
                    Schedule your comprehensive dental exam and cleaning today and take the first step towards a healthier, brighter smile!
                </Typography>
              <Button
                onClick={() => window.location.href = BOOKING_URL}
                variant="contained"
                color="secondary"
                size="large"
                sx={{
                  px: 5, 
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  '&:hover': {
                    backgroundColor: 'secondary.dark',
                  }
                }}
              >
                Book Your Appointment Now
              </Button>
              <Typography variant="body2" sx={{mt: 2, opacity: 0.8}}>
                Or call us at <Link to="tel:(408) 869-0998" style={{color: 'white', fontWeight: 'bold'}}>(408) 869-0998</Link> to speak with our friendly team.
              </Typography>
            </Box>

          </motion.div>
        </Container>
      </Box>
    </>
  );
};

export default DentalExamsAndCleanings;